import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Award,
  Building2,
  Home as HomeIcon,
  Landmark,
  Factory,
  Church,
  Wrench,
  Sparkles,
} from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import feat1 from "@/assets/feat-1.webp";
import feat2 from "@/assets/feat-2.webp";
import feat3 from "@/assets/feat-3.webp";
import feat4 from "@/assets/feat-4.webp";
import feat5 from "@/assets/feat-5.webp";
import feat6 from "@/assets/feat-6.webp";
import feat7 from "@/assets/feat-7.webp";
import feat8 from "@/assets/feat-8.webp";
import feat9 from "@/assets/feat-9.webp";
import feat10 from "@/assets/feat-10.webp";
import feat11 from "@/assets/feat-11.webp";
import feat12 from "@/assets/feat-12.webp";

import GoogleReviews from "@/components/GoogleReviews";

const featuredProjects = [
  { title: "Dr. Shanthi Residence", category: "Residential", image: feat1 },
  { title: "Cultural Center", category: "Cultural", image: feat2 },
  { title: "Memorial Building", category: "Cultural", image: feat3 },
  { title: "Memorial Museum", category: "Commercial", image: feat4 },
  { title: "Sri Balaji Bhavan", category: "Commercial", image: feat5 },
  { title: "Café Interior", category: "Interior", image: feat6 },
  { title: "Modern Night Villa", category: "Residential", image: feat7 },
  { title: "Contemporary Villa 07", category: "Residential", image: feat8 },
  { title: "Cathedral Design", category: "Cultural", image: feat9 },
  { title: "Luxury Villa 98", category: "Residential", image: feat10 },
  { title: "Organic Facade Building", category: "Unique", image: feat11 },
  { title: "St. Thomas Church", category: "Cultural", image: feat12 },
];

const categories = [
  { name: "Interior", icon: Sparkles },
  { name: "Residential", icon: HomeIcon },
  { name: "Commercial", icon: Building2 },
  { name: "Institutional", icon: Landmark },
  { name: "Industrial", icon: Factory },
  { name: "Cultural", icon: Church },
  { name: "Renovation", icon: Wrench },
  { name: "Unique", icon: Sparkles },
];

const awards = [
  "Best Residential Design 2024",
  "Green Building Excellence Award",
  "National Architecture Honor",
  "Interior Design Innovation",
  "Urban Planning Recognition",
  "Heritage Conservation Award",
];

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const heroVideos = ["/videos/hero-video-1.mp4"];

const Index = () => {
  const [videoIndex, setVideoIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  return (
    <Layout transparentNav>
      <SEOHead
        title="Registered Architect in Kumbakonam & Cauvery Delta District"
        description="Dhivisha Architects — COA-registered architecture & interior design firm in Kumbakonam, serving the Cauvery Delta region. 350+ projects, 300+ happy clients. Residential, commercial, cultural & renovation expertise across Thanjavur, Mayiladuthurai & Nagapattinam."
        path="/"
      />
      <section className="relative h-[29vh] sm:h-[60vh] md:h-screen w-full overflow-hidden bg-muted sm:bg-secondary">
        <video
          ref={videoRef}
          key={heroVideos[videoIndex]}
          src={heroVideos[videoIndex]}
          className="absolute inset-0 w-full h-full object-contain sm:object-cover"
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={() => setVideoIndex((i) => (i + 1) % heroVideos.length)}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/10 via-transparent to-transparent pointer-events-none" />
      </section>

      {/* Quick facts (answer-ready summary) */}
      <section className="py-8 sm:py-12 bg-background border-b border-border">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
            Dhivisha Architects — Registered Architect in Kumbakonam
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-3xl mx-auto">
            Dhivisha Architects &amp; Interiors is a Council of Architecture (COA) registered architecture and interior design firm in Kumbakonam, Tamil Nadu. We design homes, commercial, institutional and cultural buildings across the Cauvery Delta — Thanjavur, Mayiladuthurai, Nagapattinam, Tiruvarur and Ariyalur. 350+ projects completed for 300+ clients. Call{" "}
            <a href="tel:+919994849332" className="text-primary font-medium">+91 99948 49332</a>.
          </p>
        </div>
      </section>

      {/* Categories Strip */}
      <section className="py-10 sm:py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-foreground"
          >
            Our Expertise
          </motion.h2>
          <motion.div
            className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {categories.map((cat) => (
              <motion.div key={cat.name} variants={staggerItem}>
                <Link
                  to="/projects"
                  className="flex flex-col items-center gap-2 sm:gap-3 p-2 sm:p-4 rounded bg-card hover:shadow-lg hover:border-primary border border-transparent transition-all group active:scale-95"
                >
                  <cat.icon
                    size={24}
                    className="text-primary group-hover:scale-110 transition-transform sm:w-7 sm:h-7"
                  />
                  <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-foreground/70 text-center">
                    {cat.name}
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Top 12 Featured Projects */}
      <section className="py-12 sm:py-20 content-auto">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            className="text-center mb-8 sm:mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4">
              Our Extraordinary Projects
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
              Showcasing our extraordinary architectural and interior design work.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {featuredProjects.map((project, i) => (
              <motion.div
                key={project.title}
                variants={staggerItem}
                whileTap={{ scale: 0.98 }}
                className={`group relative overflow-hidden rounded ${i === 0 ? "col-span-2 md:col-span-2 md:row-span-2" : ""}`}
              >
                <div className={`overflow-hidden ${i === 0 ? "h-[200px] sm:h-[500px]" : "h-[140px] sm:h-[280px]"}`}>
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3 sm:p-6">
                  <div>
                    <span className="text-[10px] sm:text-xs uppercase tracking-widest text-primary font-semibold">
                      {project.category}
                    </span>
                    <h3 className="text-sm sm:text-xl font-bold text-primary-foreground mt-0.5 sm:mt-1">
                      {project.title}
                    </h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          <motion.div
            className="text-center mt-6 sm:mt-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Button asChild variant="outline" size="lg" className="active:scale-95 transition-transform">
              <Link to="/projects">
                View All Projects <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Awards Strip */}
      <section className="py-8 sm:py-12 bg-secondary content-auto">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 justify-center"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Award size={24} className="text-primary sm:w-7 sm:h-7" />
            <h2 className="text-xl sm:text-2xl font-bold text-secondary-foreground">Awards & Recognition</h2>
          </motion.div>
          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {awards.map((award) => (
              <motion.div
                key={award}
                variants={staggerItem}
                whileHover={{ y: -4 }}
                className="text-center p-3 sm:p-4 border border-secondary-foreground/10 rounded hover:border-primary/30 transition-colors"
              >
                <Award size={18} className="text-primary mx-auto mb-1.5 sm:mb-2" />
                <p className="text-[11px] sm:text-xs font-medium text-secondary-foreground/80">{award}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Google Reviews */}
      <GoogleReviews />

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-muted">
        <motion.div
          className="max-w-3xl mx-auto px-4 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4">
            Ready to Build Your Vision?
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">
            Share your requirements and our team will get back to you with the best solution.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 sm:px-10 active:scale-95 transition-transform"
          >
            <Link to="/contact">Get in Touch</Link>
          </Button>
        </motion.div>
      </section>
    </Layout>
  );
};

export default Index;
